import { useMediaQuery } from 'react-responsive';

import { MEDIA_QUERY_MIN_WIDTH } from '~shared/lib/responsive';

import cn from 'classnames';

import {
  DEFAULT_BOUNDARY_COUNT,
  DEFAULT_PAGE_SIZE_OPTIONS,
  DEFAULT_SIBLING_COUNT,
} from './lib/constants';
import { usePaginationRange } from './lib/use-pagination-range';
import { PageNumbers } from './ui/page-numbers';
import { PageSizeSelect } from './ui/page-size-select';

export type PaginationProps = {
  page: number;
  itemsTotal: number;
  pageSize: number;
  pageSizeOptions?: readonly number[];
  siblingCount?: number;
  boundaryCount?: number;
  className?: string;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
};

const clampPage = (page: number, totalPages: number): number =>
  totalPages > 0 ? Math.min(Math.max(page, 0), totalPages - 1) : 0;

export const Pagination = ({
  page,
  itemsTotal,
  pageSize,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  siblingCount = DEFAULT_SIBLING_COUNT,
  boundaryCount = DEFAULT_BOUNDARY_COUNT,
  className,
  onPageChange,
  onPageSizeChange,
}: PaginationProps) => {
  const isDesktop = useMediaQuery({ query: MEDIA_QUERY_MIN_WIDTH.lg });
  const totalPages = pageSize > 0 ? Math.ceil(itemsTotal / pageSize) : 0;
  const safePage = clampPage(page, totalPages);

  const items = usePaginationRange({
    totalPages,
    currentPage: safePage,
    siblingCount: isDesktop ? siblingCount : Math.max(0, siblingCount - 1),
    boundaryCount,
  });

  if (itemsTotal <= 0) {
    return null;
  }

  return (
    <div className={cn('flex flex-col items-center gap-6', className)}>
      <PageSizeSelect
        value={pageSize}
        options={pageSizeOptions}
        onPageSizeChange={onPageSizeChange}
      />
      {totalPages > 1 && (
        <PageNumbers
          items={items}
          currentPage={safePage}
          totalPages={totalPages}
          onPageChange={onPageChange}
          size={isDesktop ? 'l' : 'm'}
        />
      )}
    </div>
  );
};
